/**
 * @file Leads export download service.
 * @description Fetches backend-generated CSV files (export is created on the server).
 */
import { buildApiUrl, getAuthToken } from '../utils/api';

function toUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  return buildApiUrl(pathOrUrl.replace(/^\/api\//, '').replace(/^\//, ''));
}

function pickDownloadUrl(json: Record<string, unknown>): string {
  const data = json['data'];
  const block =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : null;

  const downloadUrl = String(block?.['download_url'] ?? json['download_url'] ?? '').trim();
  if (downloadUrl) return downloadUrl;

  const fileName = String(block?.['file_name'] ?? json['file_name'] ?? '').trim();
  return fileName ? toUrl(`view-leads/download/${fileName}`) : '';
}

async function fetchExportBlob(url: string, depth = 0): Promise<Blob> {
  if (depth > 1) throw new Error('Download failed');

  const token = getAuthToken();
  const response = await fetch(toUrl(url), {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);

  const text = await response.text();
  if (!text.trim().startsWith('{')) {
    return new Blob([text], { type: 'text/csv;charset=utf-8' });
  }

  const json = JSON.parse(text) as Record<string, unknown>;
  const nextUrl = pickDownloadUrl(json);
  if (!nextUrl) {
    throw new Error(String(json['message'] || 'No download URL in export response'));
  }
  return fetchExportBlob(nextUrl, depth + 1);
}

export class LeadsExportService {
  static downloadExportedLeadsFile(fileName: string): Promise<Blob> {
    return fetchExportBlob(`view-leads/download/${fileName}`);
  }

  static downloadFromExportUrl(exportUrl: string): Promise<Blob> {
    return fetchExportBlob(exportUrl);
  }
}
